import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, ArrowRight } from "lucide-react";
import { SITE, API_URL } from "../data/site";

const inputCls =
  "w-full rounded-xl border border-white/12 bg-[#04120E]/70 px-4 py-3 text-sm text-white placeholder:text-emerald-100/35 outline-none focus:border-[#00E676]/60 focus:ring-2 focus:ring-[#00E676]/25 transition-[border-color,box-shadow] duration-300";

export const LeadPopup = () => {
  const [open, setOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", whatsapp: "", consent: true });

  useEffect(() => {
    if (localStorage.getItem("igreen_popup_seen")) return;
    const t = setTimeout(() => setOpen(true), 12000);
    return () => clearTimeout(t);
  }, []);

  const close = () => {
    localStorage.setItem("igreen_popup_seen", "1");
    setOpen(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.whatsapp.trim()) {
      toast.error("Informe seu nome e WhatsApp.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API_URL}/leads`, {
        name: form.name.trim(),
        email: form.email || null,
        whatsapp: form.whatsapp,
        source: "popup",
      });
      toast.success("Dados enviados! Em breve falo com você.");
      close();
    } catch {
      toast.error("Não foi possível enviar agora. Tente pelo WhatsApp.");
    } finally {
      setSending(false);
    }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[60] grid place-items-center p-5" data-testid="lead-popup">
      <div className="absolute inset-0 bg-[#04120E]/80 backdrop-blur-sm" onClick={close} />
      <div className="relative w-full max-w-md rounded-3xl border border-[#00E676]/25 bg-[#0B261E] p-8 shadow-2xl">
        <button
          onClick={close}
          data-testid="lead-popup-close-button"
          aria-label="Fechar"
          className="absolute top-4 right-4 p-2 rounded-full text-emerald-100/60 hover:text-white hover:bg-white/10 transition-colors"
        >
          <X size={18} />
        </button>
        <div className="flex items-center gap-3.5">
          <img
            src={SITE.photo}
            alt={SITE.licenciado}
            data-testid="lead-popup-photo"
            className="w-14 h-14 rounded-full object-cover border-2 border-[#00E676]/40 shadow-[0_0_16px_rgba(0,230,118,0.3)]"
          />
          <p className="text-[11px] font-mono2 uppercase tracking-[0.14em] text-[#00E676] leading-snug">
            {SITE.licenciado}<br />
            <span className="text-emerald-200/50">{SITE.grupo} · Licenciados iGreen</span>
          </p>
        </div>
        <h3 className="mt-3 font-heading text-2xl font-bold text-white">Antes de continuar</h3>
        <p className="mt-1.5 text-sm text-emerald-100/55">Deixe seu nome e WhatsApp.</p>
        <form onSubmit={submit} className="mt-6 space-y-4">
          <input
            data-testid="lead-popup-name-input"
            placeholder="Seu nome"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className={inputCls}
            required
          />
          <input
            data-testid="lead-popup-email-input"
            type="email"
            placeholder="Seu e-mail"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className={inputCls}
          />
          <input
            data-testid="lead-popup-whatsapp-input"
            placeholder="Seu WhatsApp com DDD"
            value={form.whatsapp}
            onChange={(e) => setForm({ ...form, whatsapp: e.target.value })}
            className={inputCls}
            required
          />
          <label className="flex items-start gap-2.5 text-[11px] text-emerald-100/50 leading-relaxed cursor-pointer">
            <input
              type="checkbox"
              data-testid="lead-popup-consent-checkbox"
              checked={form.consent}
              onChange={(e) => setForm({ ...form, consent: e.target.checked })}
              className="mt-0.5 accent-[#00E676]"
            />
            Aceito receber contato de {SITE.licenciado} ({SITE.grupo}), licenciados iGreen, pelo WhatsApp.{" "}
            <a href="/privacidade" className="text-[#00E676] underline underline-offset-2" data-testid="lead-popup-privacy-link">Política de privacidade</a>.
          </label>
          <button
            type="submit"
            disabled={sending || !form.consent}
            data-testid="lead-popup-submit-button"
            className="w-full inline-flex justify-center items-center gap-2 rounded-full bg-[#00E676] hover:bg-[#00C853] disabled:opacity-60 text-[#04120E] font-bold px-6 py-3.5 transition-[background-color,opacity] duration-300"
          >
            {sending ? "Enviando…" : "Continuar"} <ArrowRight size={16} />
          </button>
        </form>
        <p className="mt-4 text-[10px] text-emerald-100/35 leading-relaxed">
          {SITE.licenciado} · Licenciados Autorizados iGreen Energy (ID {SITE.licenciadoId}). Seus dados ficam só com a gente, para te atender.
        </p>
      </div>
    </div>
  );
};
